import { Component, Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth, LoginPage } from './components/Auth'
import AgeConfirmationGate from './components/AgeConfirmationGate'
import { needsAgeConfirmation } from './lib/ageGate'
import { SettingsProvider } from './components/SettingsContext'
import { SetupWizardProvider } from './components/SetupWizard'
import { ToastProvider } from './components/ToastContext'
import { queryClient } from './lib/queryClient'
import { handleChunkLoadError, isChunkLoadError } from './lib/chunkRecovery'
import { builderDeckIdFromPath } from './lib/builderRoute'
import { applyRouteHead } from './lib/seoRoutes'
import { isDeckPubliclyViewable } from './lib/deckViewData'
import { AppBootSkeleton } from './components/Skeletons'
import Layout from './components/Layout'
import MilestoneWatcher from './components/MilestoneWatcher'
import AnnouncementWatcher from './components/AnnouncementWatcher'
import PriceAlertWatcher from './components/PriceAlertWatcher'

class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error) {
    handleChunkLoadError(error)
  }

  render() {
    if (!this.state.error) return this.props.children
    const isChunkError = isChunkLoadError(this.state.error)
    return (
      <div style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'var(--bg)',
        color: 'var(--text)',
      }}>
        <div style={{
          maxWidth: 420,
          width: '100%',
          border: '1px solid var(--border)',
          borderTop: '3px solid var(--gold)',
          borderRadius: 8,
          background: 'var(--bg2)',
          padding: 20,
          boxShadow: '0 18px 50px rgba(0,0,0,0.24)',
        }}>
          <h1 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>
            {isChunkError ? 'Update needed' : 'Something went wrong'}
          </h1>
          <p style={{ margin: '0 0 16px', color: 'var(--text-dim)', lineHeight: 1.45 }}>
            {isChunkError
              ? 'DeckLoom was updated while this tab was open. Reload to fetch the latest files.'
              : 'Reload the app and try again.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              border: '1px solid var(--border-hi)',
              borderRadius: 6,
              background: 'var(--gold)',
              color: '#15120a',
              fontWeight: 700,
              padding: '10px 14px',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    )
  }
}

const HomePage = lazy(() => import('./pages/Home'))
const CollectionPage = lazy(() => import('./pages/Collection'))
const FoldersPage = lazy(() => import('./pages/Folders'))
const ListsPage = lazy(() => import('./pages/Lists'))
const StatsPage = lazy(() => import('./pages/Stats'))
const SharePage = lazy(() => import('./pages/Share'))
const TradePage = lazy(() => import('./pages/Trade'))
const JoinGamePage = lazy(() => import('./pages/JoinGame'))
const JoinTournamentPage = lazy(() => import('./pages/JoinTournament'))
const SettingsPage = lazy(() => import('./pages/Settings'))
const HelpPage = lazy(() => import('./pages/Help'))
const AdminPage = lazy(() => import('./pages/Admin'))
const LegalPage = lazy(() => import('./pages/Legal'))
const TermsPage = lazy(() => import('./pages/Terms'))
const PrivacyPage = lazy(() => import('./pages/Privacy'))
const StorageNoticePage = lazy(() => import('./pages/StorageNotice'))
const CreditsPage = lazy(() => import('./pages/Credits'))
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccount'))
const LifeTrackerPage = lazy(() => import('./pages/LifeTracker'))
const BuilderPage = lazy(() => import('./pages/Builder'))
const DeckBuilderPage = lazy(() => import('./pages/DeckBuilder'))
const DeckGoldfishPage = lazy(() => import('./pages/DeckGoldfish'))
const DeckViewPage = lazy(() => import('./pages/DeckView'))
const TradingPage = lazy(() => import('./pages/Trading'))
const TournamentsPage = lazy(() => import('./pages/Tournaments'))
const ScannerPage = lazy(() => import('./pages/Scanner'))
const ProfilePage = lazy(() => import('./pages/Profile'))
const RulebookPage = lazy(() => import('./pages/Rulebook'))
const UpcomingSetsPage = lazy(() => import('./pages/UpcomingSets'))
const SetSpoilerPage = lazy(() => import('./pages/SetSpoiler'))

// A signed-out visitor on a builder deck URL: someone shared the editor link
// instead of the /d/:id share link. A public deck goes to the public view; a
// private or unknown one falls through to the normal login page, deliberately
// still at /builder/:id — so signing in lands the owner in their own builder
// rather than a read-only copy of it.
function SignedOutBuilderDeck({ deckId }) {
  const [isPublic, setIsPublic] = useState(null)

  useEffect(() => {
    let active = true
    setIsPublic(null)
    isDeckPubliclyViewable({ id: deckId }).then(ok => { if (active) setIsPublic(ok) })
    return () => { active = false }
  }, [deckId])

  // AppBootSkeleton paints nothing for its first 150 ms, so a fast answer here
  // never flashes a loading state on the way to the login page.
  if (isPublic === null) return <AppBootSkeleton />
  if (isPublic) return <Navigate to={`/d/${deckId}`} replace />
  return <LoginPage />
}

// Keeps the document head on the current route. The prerendered page a
// visitor lands on (see src/lib/seoRoutes.js) carries its own title and
// canonical, and without this they would stick through SPA navigation.
// Rendered before <Routes> so a page that sets its own title wins.
function RouteHeadSync() {
  const { pathname } = useLocation()
  useEffect(() => { applyRouteHead(pathname) }, [pathname])
  return null
}

function PrivateApp() {
  const { user, authEvent } = useAuth()
  const { pathname } = useLocation()
  if (authEvent === 'PASSWORD_RECOVERY') return <LoginPage forcedMode="recovery" />
  if (!user) {
    const builderDeckId = builderDeckIdFromPath(pathname)
    if (builderDeckId) return <SignedOutBuilderDeck deckId={builderDeckId} />
    return <LoginPage />
  }
  // Checked after authentication rather than on the signup form, because the
  // social buttons create accounts without the form ever rendering.
  if (needsAgeConfirmation(user)) return <AgeConfirmationGate user={user} />
  return (
    <SettingsProvider>
      <SetupWizardProvider>
      <MilestoneWatcher />
      <AnnouncementWatcher />
      <PriceAlertWatcher />
      <Layout>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/"            element={<HomePage />} />
            <Route path="/collection"  element={<CollectionPage />} />
            <Route path="/decks"       element={<FoldersPage key="decks"   type="deck" />} />
            <Route path="/binders"     element={<FoldersPage key="binders" type="binder" />} />
            <Route path="/lists"       element={<ListsPage />} />
            <Route path="/trading"     element={<TradingPage />} />
            <Route path="/stats"       element={<StatsPage />} />
            <Route path="/life"        element={<LifeTrackerPage />} />
            <Route path="/tournaments" element={<TournamentsPage />} />
            <Route path="/settings"    element={<SettingsPage />} />
            <Route path="/help"        element={<HelpPage />} />
            <Route path="/rules"       element={<RulebookPage />} />
            <Route path="/admin"       element={<AdminPage />} />
            <Route path="/discover"    element={<Navigate to="/builder?tab=browser" replace />} />
            <Route path="/builder"     element={<BuilderPage />} />
            <Route path="/builder/:id/playtest" element={<DeckGoldfishPage />} />
            <Route path="/builder/:id" element={<DeckBuilderPage />} />
            <Route path="/scanner"          element={<ScannerPage />} />
            <Route path="/profile/:username" element={<ProfilePage />} />
            <Route path="*"                 element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Layout>
      </SetupWizardProvider>
    </SettingsProvider>
  )
}

export default function App() {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ChunkErrorBoundary>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <RouteHeadSync />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/storage" element={<StorageNoticePage />} />
                  <Route path="/credits" element={<CreditsPage />} />
                  <Route path="/sets" element={<UpcomingSetsPage />} />
                  <Route path="/sets/:code" element={<SetSpoilerPage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />
                  <Route path="/share/:token" element={<SharePage />} />
                  <Route path="/trade/:username" element={<TradePage />} />
                  <Route path="/d/:id" element={<DeckViewPage />} />
                  <Route path="/join/:code" element={<JoinGamePage />} />
                  <Route path="/join-tournament/:code" element={<JoinTournamentPage />} />
                  <Route path="/*" element={<PrivateApp />} />
                </Routes>
              </Suspense>
            </ToastProvider>
          </QueryClientProvider>
        </AuthProvider>
      </ChunkErrorBoundary>
    </BrowserRouter>
  )
}
